import { useCallback, useEffect, useState } from "react";
import * as ml from "../../api/ml";
import { listFolders } from "../../api/images";
import { getProject } from "../../api/projects";
import type { FolderList, Project } from "../../api/types";
import { creditsChanged } from "../../lib/credits";
import { navigate } from "../../router";
import { projectDataChanged } from "../../state/currentProject";
import { showInGallery } from "../../state/handoff";
import { useSession } from "../../state/session";
import { ModelVersionPicker } from "../../ui/ModelVersionPicker";
import { useModelActions } from "../../ui/useModelActions";
import { Icon } from "../../ui/icons";

/**
 * Auto-annotate: let a trained model annotate the images nobody has annotated yet. Three choices (which version, how
 * sure it must be, which images) and one button; what it finds waits for review before training uses it. Training is
 * on the Train page and the model files on the Models page, so this page does only this.
 */
export function AutoScreen({ projectId, version }: { projectId: number; /** Models ▸ Auto-annotate with this version. */ version?: number }) {
  const { canEdit } = useSession();
  const [project, setProject] = useState<Project | null>(null);
  const [stats, setStats] = useState<ml.AnnotationStats | null>(null);
  const [statsError, setStatsError] = useState("");
  const [conf, setConf] = useState(15);
  const [folders, setFolders] = useState<FolderList | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  const [batch, setBatch] = useState<{ running: boolean; text: string; error: boolean; made: number }>({ running: false, text: "", error: false, made: 0 });
  const [stopping, setStopping] = useState(false);

  const refresh = useCallback(async () => {
    try { setStats(await ml.annotationStats(projectId)); setStatsError(""); }
    catch (e) { setStats(null); setStatsError(`Could not load the project's counts: ${(e as Error).message}`); }
  }, [projectId]);

  const model = useModelActions({ id: projectId, name: project?.name ?? "model" }, stats?.latest_model_version ?? null, refresh, version ?? null);

  useEffect(() => {
    let alive = true;
    getProject(projectId).then((p) => alive && setProject(p)).catch(() => {});
    listFolders(projectId).then((f) => alive && setFolders(f)).catch(() => {});
    void refresh();
    return () => { alive = false; };
  }, [projectId, refresh]);

  const hasModel = !!stats?.has_trained_model;
  const using = model.picked ?? stats?.latest_model_version ?? null;
  const where = folder === null ? "" : folder === "" ? " without a folder" : ` in “${folder}”`;
  const buttonText = folder === null ? "Auto-annotate all unannotated images" : folder === "" ? "Auto-annotate unannotated images (no folder)" : `Auto-annotate unannotated images in “${folder}”`;

  const run = async () => {
    setBatch({ running: true, error: false, made: 0, text: `Annotating the unannotated images${where} with model v${using}…` });
    try {
      const r = await ml.autoAnnotateBatch(projectId, conf / 100, folder, model.picked);
      const made = r.total_annotations ?? 0;
      setBatch({
        running: false, error: r.stopped === "credits", made,
        text: (r.message ?? "Done.") + (made ? "\nCheck what it found and correct any mistakes: only reviewed annotations are used for training." : ""),
      });
      void refresh(); projectDataChanged();
    } catch (e) { setBatch({ running: false, error: true, made: 0, text: `Error: ${(e as Error).message}` }); }
    finally { creditsChanged(); setStopping(false); }
  };
  const stop = async () => {
    setStopping(true);
    try { await ml.cancelAutoAnnotateBatch(projectId); } catch { /* it finished meanwhile */ }
  };
  const review = () => { showInGallery(projectId, { folder: null, status: "auto_annotated" }); navigate({ name: "dashboard" }); };

  const waiting = stats?.auto_annotated ?? 0;
  const status = batch.text || (stats ? (hasModel ? `Model v${using} is ready.` : "") : statsError);

  return (
    <div className="train-page" data-testid="auto-page">
      <div className="page-head">
        <div>
          <h1 className="page-title" data-testid="auto-title">Auto-annotate — {project?.name ?? ""}</h1>
          <div className="muted">Let a trained model annotate the images nobody has annotated yet. You review what it found; only reviewed annotations train the next model.</div>
        </div>
      </div>

      {stats && !hasModel ? (
        <section className="card-section empty-models" data-testid="auto-no-model">
          <Icon name="wand" size={28} />
          <h2>No model yet</h2>
          <div className="muted">Auto-annotating needs a trained model. Train one from your reviewed annotations, or import weights on the Models page.</div>
          <div className="row" style={{ justifyContent: "center" }}>
            <button className="btn primary" onClick={() => navigate({ name: "train", projectId })} data-testid="auto-go-train"><Icon name="target" />Go to Train</button>
            <button className="btn" onClick={() => navigate({ name: "models", projectId })} data-testid="auto-go-models"><Icon name="cube" />Models</button>
          </div>
        </section>
      ) : (
        <div className="train-grid">
          <div className="train-main">
            <section className="card-section" data-testid="auto-settings">
              <div className="section-head"><h2>Settings</h2>
                <button className="btn ghost" onClick={() => navigate({ name: "models", projectId })} data-testid="auto-models">Models →</button></div>
              <div className="field-stack">
                <div className="field">
                  <label>Model</label>
                  {model.versions.length > 1
                    ? <ModelVersionPicker versions={model.versions} picked={model.picked} onPick={model.setPicked} testId="auto-version" title="Which version annotates." />
                    : <div data-testid="auto-model-line">{using ? `v${using}, the only version` : "…"}</div>}
                </div>
                <div className="field">
                  <label htmlFor="auto-conf">How sure the model must be</label>
                  <div className="row" style={{ flexWrap: "nowrap" }}>
                    <input id="auto-conf" className="input" type="number" min={10} max={90} value={conf} style={{ width: 80 }} data-testid="auto-conf"
                      onChange={(e) => setConf(Math.min(90, Math.max(10, Math.trunc(Number(e.target.value)) || 10)))} /><span className="muted">%</span>
                  </div>
                  <div className="muted">Lower finds more objects, with more mistakes to correct. Higher finds fewer, more surely. 15% suits a first pass you will review.</div>
                </div>
                <div className="field">
                  <label htmlFor="auto-folder">Which images</label>
                  <select id="auto-folder" className="select" style={{ maxWidth: 360 }} value={folder === null ? "" : folder === "" ? "__none__" : folder} data-testid="auto-folder"
                    title="Run over one folder only, to keep an image set (a ground-truth batch, say) apart from the rest."
                    onChange={(e) => setFolder(e.target.value === "" ? null : e.target.value === "__none__" ? "" : e.target.value)}>
                    <option value="">All images in project</option>
                    {(folders?.folders ?? []).map((f) => <option key={f.folder} value={f.folder}>{`${f.folder}  (${f.image_count})`}</option>)}
                    {!!folders?.unfoldered && <option value="__none__">{`No folder  (${folders.unfoldered})`}</option>}
                  </select>
                  <div className="muted">Only images with no annotations are touched; annotated ones are left as they are.</div>
                </div>
              </div>
            </section>
          </div>

          <aside className="train-run">
            <div className="run-card" data-testid="auto-run">
              <div className="tile-label">Run</div>
              <div className="run-summary" data-testid="auto-counts">
                {stats ? `${stats.unannotated} image${stats.unannotated === 1 ? "" : "s"} not annotated yet · ${waiting} waiting for review` : "Checking…"}
              </div>
              <button className="btn primary run-go" disabled={!hasModel || !canEdit || batch.running} onClick={run} data-testid="auto-batch">
                {batch.running ? "Auto-annotating…" : <><Icon name="wand" />{buttonText}</>}</button>
              {batch.running && (
                <>
                  <div className="progress indeterminate" aria-label="Working"><i /></div>
                  <button className="btn danger" disabled={stopping} onClick={stop} data-testid="auto-batch-stop"
                    title="Stop after the images in progress. What is already annotated is kept.">{stopping ? "Stopping…" : <><Icon name="stop" />Stop</>}</button>
                </>
              )}
              {status && <div className={batch.error ? "warning-text" : "run-status"} style={{ whiteSpace: "pre-wrap" }} data-testid="auto-batch-status">{status}</div>}
              {!batch.running && waiting > 0 && (
                <button className="btn" onClick={review} data-testid="auto-review"><Icon name="check" />Review the {waiting} image{waiting === 1 ? "" : "s"} now</button>
              )}
            </div>
          </aside>
        </div>
      )}
      {model.panel}
    </div>
  );
}
